/**
 * streamsBootstrap — module-scope singleton that owns the Amazon Connect Streams
 * SDK subscriptions. Must be invoked exactly once per window. Every SDK event
 * is dispatched into `streamsStore`; no React code subscribes to the SDK directly.
 */
import { connectClient, isConnectLoaded, terminateCcp, EventType } from './connectClient';
import { streamsStore } from './streamsStore';
import { ccpLogger, callLogger } from '../Utils/ccpLogger';
import {
    ingestCcpTelemetryLog,
    ingestSoftphoneReportEvent,
    ingestSoftphoneStatsEvent,
} from './agentNetworkTelemetry/webrtcTelemetryBridge';
import { attachCcpWebSocketTelemetry, resetCcpWebSocketTelemetry } from './agentNetworkTelemetry/ccpEvents';

let _initCalled = false;
let _agentCbRegistered = false;
let _contactCbRegistered = false;
let _agentHandlersRegistered = false;
let _lastBootstrapConfig: BootstrapConfig | null = null;

export interface BootstrapConfig {
    container: HTMLElement;
    ccpUrl: string;
    region?: string;
}

function patchCcpIframe(container: HTMLElement) {
    const existing = container.querySelector('iframe');
    if (existing) {
        existing.setAttribute('allow', 'microphone *; autoplay *; speaker-selection *');
        return;
    }

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((m) => {
            Array.from(m.addedNodes).forEach((node) => {
                if (node instanceof HTMLIFrameElement) {
                    node.setAttribute('allow', 'microphone *; autoplay *; speaker-selection *');
                    observer.disconnect();
                }
            });
        });
    });

    observer.observe(container, { childList: true });
}

function suppressPostMessageNullErrors() {
    const handler = (event: ErrorEvent) => {
        if (event.message?.includes('postMessage') && event.message?.includes('null')) {
            event.preventDefault();
            return true;
        }
    };
    window.addEventListener('error', handler);
}

function buildCcpConfig(cfg: BootstrapConfig) {
    const popupWidth = 460;
    const popupHeight = 640;
    const popupLeft = Math.max(0, Math.round((window.screen.width - 1000) / 2));
    const popupTop = Math.max(0, Math.round((window.screen.height - popupHeight) / 2));

    return {
        ccpUrl: cfg.ccpUrl,
        region: cfg.region || 'us-east-1',
        loginPopup: false,
        loginPopupAutoClose: true,
        loginUrl: cfg.ccpUrl,
        loginOptions: {
            autoClose: true,
            height: popupHeight,
            width: popupWidth,
            top: popupTop,
            left: popupLeft,
            forceWindow: true,
            disableAuthPopupAfterLogout: true,
        },
        softphone: {
            allowFramedSoftphone: true,
            disableRingtone: false,
            allowEarlyGum: true,
            allowFramedVideoCall: false,
        },
        pageOptions: {
            enableAudioDeviceSettings: false,
            enableVideoDeviceSettings: true,
            enablePhoneTypeSettings: true,
            showInactivityModal: false,
        },
        ccpAckTimeout: 8000,
        ccpSynTimeout: 3000,
        ccpLoadTimeout: 10000,
        shouldAddNamespaceToLogs: true,
        logConfig: {
            logLevel: connectClient.LogLevel.WARN,
            echoLevel: connectClient.LogLevel.WARN,
        },
    };
}

function resetBootstrapFlags() {
    _initCalled = false;
    _agentCbRegistered = false;
    _contactCbRegistered = false;
    _agentHandlersRegistered = false;
    try {
        resetCcpWebSocketTelemetry();
    } catch {
        /* telemetry only */
    }
}

function attachStreamsErrorLogForwarding() {
    try {
        const logger = connectClient.getLog?.();
        if (!logger || (logger as any).__adastraErrorForwarding) return;
        (logger as any).__adastraErrorForwarding = true;

        const readLogText = (logEntry: any): string => {
            try {
                return (
                    logEntry?.getText?.() ??
                    logEntry?.text ??
                    (typeof logEntry?.toString === 'function' ? logEntry.toString() : String(logEntry ?? ''))
                );
            } catch {
                return '';
            }
        };

        const captureWebRtc = (logEntry: any) => {
            try {
                ingestCcpTelemetryLog(readLogText(logEntry));
            } catch {
                /* never break Streams logging */
            }
        };

        const forwardError = (logEntry: any) => {
            try {
                const level = String(
                    logEntry?.getLevel?.() ?? logEntry?.level ?? '',
                ).toUpperCase();
                if (level !== 'ERROR' && level !== 'CRITICAL') return;

                const text =
                    logEntry?.getText?.() ??
                    logEntry?.text ??
                    (typeof logEntry?.toString === 'function'
                        ? logEntry.toString()
                        : String(logEntry));

                ccpLogger.error(text, {
                    source: 'connect.getLog',
                    streamsLevel: level,
                    component: logEntry?.getComponent?.() ?? logEntry?.component ?? null,
                    agentResourceId:
                        logEntry?.getAgentResourceId?.() ?? logEntry?.agentResourceId ?? null,
                    streamsLog:
                        typeof logEntry?.toString === 'function' ? logEntry.toString() : null,
                });
            } catch {
                /* never break Streams logging */
            }
        };

        const originalAdd = logger.addLogEntry?.bind(logger);
        if (typeof originalAdd === 'function') {
            logger.addLogEntry = (logEntry: any) => {
                originalAdd(logEntry);
                captureWebRtc(logEntry);
                forwardError(logEntry);
            };
        }

        const originalSend = logger.sendInternalLogEntryToServer?.bind(logger);
        if (typeof originalSend === 'function') {
            logger.sendInternalLogEntryToServer = (logEntry: any) => {
                originalSend(logEntry);
                captureWebRtc(logEntry);
                forwardError(logEntry);
            };
        }
    } catch {
        /* ignore */
    }
}

function registerCoreHandlers() {
    try {
        const eventBus = connectClient.core.getEventBus();
        if (eventBus) {
            (eventBus as any).subscribe('ack_done', () => {
                ccpLogger.info('ACK_DONE');
                streamsStore.onAckDone();
            });
            eventBus.subscribe(EventType.ACK_TIMEOUT, () => {
                ccpLogger.warn('ACK_TIMEOUT');
                streamsStore.onAckTimeout();
            });
            eventBus.subscribe(EventType.TERMINATED, () => {
                ccpLogger.warn('TERMINATED');
                streamsStore.onTerminated();
            });
            eventBus.subscribe(EventType.SOFTPHONE_STATS, (data: unknown) => {
                ingestSoftphoneStatsEvent(data);
            });
            eventBus.subscribe(EventType.SOFTPHONE_REPORT, (data: unknown) => {
                ingestSoftphoneReportEvent(data);
            });
        }
    } catch (e) {
        ccpLogger.error('Error subscribing to event bus', { error: String(e) });
        streamsStore.onAckTimeout();
    }

    const core = connectClient.core as {
        onInitialized?: (cb: () => void) => void;
        onAuthorizeSuccess?: (cb: () => void) => void;
        onAuthorizeRetriesExhausted?: (cb: () => void) => void;
        onCTIAuthorizeRetriesExhausted?: (cb: () => void) => void;
        onIframeRetriesExhausted?: (cb: () => void) => void;
        onAuthFail: (cb: () => void) => void;
        onAccessDenied: (cb: () => void) => void;
    };

    try {
        core.onInitialized?.(() => {
            ccpLogger.info('onInitialized — CCP iframe ready');
            streamsStore.onCcpInitialized();
        });
        core.onAuthorizeSuccess?.(() => {
            ccpLogger.info('onAuthorizeSuccess');
            streamsStore.onAuthorizeSuccess();
        });
        core.onAuthFail(() => {
            ccpLogger.warn('onAuthFail');
            streamsStore.onAuthFail();
        });
        core.onAccessDenied(() => {
            ccpLogger.warn('onAccessDenied');
            streamsStore.onAccessDenied();
        });
        core.onAuthorizeRetriesExhausted?.(() => {
            ccpLogger.error('onAuthorizeRetriesExhausted');
            streamsStore.onAuthorizeRetriesExhausted();
        });
        core.onCTIAuthorizeRetriesExhausted?.(() => {
            ccpLogger.error('onCTIAuthorizeRetriesExhausted');
            streamsStore.onCTIAuthorizeRetriesExhausted();
        });
        core.onIframeRetriesExhausted?.(() => {
            ccpLogger.warn('CCP iframe retries exhausted');
            streamsStore.onIframeRetriesExhausted();
        });
    } catch (e) {
        ccpLogger.warn('Error attaching core auth handlers', { error: String(e) });
    }

    try {
        const logger = connectClient.getLog();
        if (logger) {
            // Keep ERROR+ in memory/console so Softphone/CCP failures are visible,
            // then forward those ERROR lines to Sentry via attachStreamsErrorLogForwarding.
            logger.setLogLevel(connectClient.LogLevel.WARN);
            logger.setEchoLevel(connectClient.LogLevel.WARN);
        }
        attachStreamsErrorLogForwarding();
        try {
            attachCcpWebSocketTelemetry(() =>
                (connectClient.core as {getWebSocketManager?: () => {
                    onConnectionLost?: (cb: () => void) => void
                    onConnectionGain?: (cb: () => void) => void
                } | null}).getWebSocketManager?.() ?? null,
            );
        } catch {
            /* telemetry only */
        }
    } catch { /* ignore */ }

    try {
        (connectClient as { onWebSocketInitFailure?: (cb: () => void) => void })
            .onWebSocketInitFailure?.(() => streamsStore.onWebSocketInitFailure());
    } catch (e) {
        ccpLogger.warn('Error attaching onWebSocketInitFailure handler', { error: String(e) });
    }
}

function registerAgentSubscription() {
    if (_agentCbRegistered) return;
    _agentCbRegistered = true;

    connectClient.agent((agent: connect.Agent) => {
        streamsStore.onAgentInit(agent);

        // Per-agent handlers are additive; register them exactly once.
        if (_agentHandlersRegistered) return;
        _agentHandlersRegistered = true;

        try {
            agent.onStateChange((...args: any[]) => streamsStore.onAgentStateChange(args[0]));
            agent.onMuteToggle((...args: any[]) => streamsStore.onMuteToggle(args[0]));
            agent.onRefresh((...args: any[]) => streamsStore.onAgentRefresh(args[0]));
            agent.onOffline(() => streamsStore.onAgentOffline());
            (agent as any).onSoftphoneError?.((err: any) => streamsStore.onSoftphoneError(err));
            (agent as any).onError?.((agentObj: connect.Agent) => streamsStore.onAgentError(agentObj));
            (agent as any).onVoiceEnhancementModeChanged?.((evt: any) => {
                streamsStore.onVoiceEnhancementModeChanged(evt?.voiceEnhancementMode);
            });
            (agent as any).onMicrophoneDeviceChanged?.((evt: any) => {
                streamsStore.onMicrophoneDeviceChanged(evt?.deviceId);
            });
            (agent as any).onSpeakerDeviceChanged?.((evt: any) => {
                streamsStore.onSpeakerDeviceChanged(evt?.deviceId);
            });
            (agent as any).onNetworkConnectionStatusChanged?.((event: any) => {
                streamsStore.onNetworkConnectionStatusChanged(event);
            });
        } catch (e) {
            ccpLogger.error('Error attaching agent handlers', { error: String(e) });
        }
    });
}

function registerContactSubscription() {
    if (_contactCbRegistered) return;
    _contactCbRegistered = true;

    connectClient.contact((contact: connect.Contact) => {
        const contactId = contact.getContactId();
        streamsStore.onContactDiscovered(contact);

        // Per-contact handlers are auto-cleaned when the contact is destroyed,
        // so no bookkeeping set is needed.
        try {
            contact.onConnecting(() => streamsStore.onConnecting(contactId));
            contact.onIncoming(() => streamsStore.onIncomingNonVoice(contactId));
            contact.onAccepted(() => streamsStore.onAccepted(contactId));
            contact.onConnected(() => streamsStore.onConnected(contactId));
            contact.onRefresh((c: connect.Contact) => streamsStore.onContactRefresh(contactId, c));
            contact.onMissed((c: connect.Contact) => streamsStore.onMissed(contactId, c));
            (contact as any).onACW?.(() => streamsStore.onACW(contactId));
            contact.onEnded(() => streamsStore.onEnded(contactId));
            contact.onDestroy(() => streamsStore.onDestroy(contactId));
            (contact as any).onError?.((err: any) => streamsStore.onContactError(contactId, err));
        } catch (e) {
            callLogger.error('Error attaching contact handlers', { contactId, error: String(e) });
        }
    });
}

async function waitForSdk(timeoutMs = 10000): Promise<boolean> {
    if (isConnectLoaded() && typeof connectClient?.core?.getEventBus === 'function') {
        return true;
    }
    const started = Date.now();
    return new Promise((resolve) => {
        const id = setInterval(() => {
            if (isConnectLoaded() && typeof connectClient?.core?.getEventBus === 'function') {
                clearInterval(id);
                resolve(true);
                return;
            }
            if (Date.now() - started > timeoutMs) {
                clearInterval(id);
                resolve(false);
            }
        }, 100);
    });
}

export const streamsBootstrap = {
    /**
     * Initialize CCP and attach all SDK subscriptions. Idempotent — subsequent
     * calls are no-ops. Must be called from inside a React effect that has
     * mounted the container DOM element.
     */
    async init(cfg: BootstrapConfig): Promise<void> {
        if (_initCalled) {
            ccpLogger.breadcrumb('streams.bootstrap.init_duplicate_blocked');
            return;
        }
        _initCalled = true;
        _lastBootstrapConfig = cfg;

        if (!isConnectLoaded()) {
            ccpLogger.warn('streams SDK not loaded — waiting');
            const ok = await waitForSdk();
            if (!ok) {
                ccpLogger.error('streams SDK never loaded — init aborted');
                _initCalled = false;
                return;
            }
        }

        // Bail out if the container already contains a CCP iframe (e.g. HMR).
        const existing = cfg.container.querySelector('iframe');
        if (existing) {
            ccpLogger.breadcrumb('CCP iframe already present in container — skipping initCCP');
            registerCoreHandlers();
            registerAgentSubscription();
            registerContactSubscription();
            streamsStore.onCcpInitialized();
            return;
        }

        suppressPostMessageNullErrors();

        const ccpConfig = buildCcpConfig(cfg);

        try {
            ccpLogger.info('Calling initCCP');
            connectClient.core.initCCP(cfg.container, ccpConfig as any);
            patchCcpIframe(cfg.container);
        } catch (e) {
            ccpLogger.error('initCCP threw', { error: String(e) });
            streamsStore.onAckTimeout();
            _initCalled = false;
            return;
        }

        registerCoreHandlers();
        registerAgentSubscription();
        registerContactSubscription();
    },

    /**
     * Uninitialize Streams, remove the CCP iframe, and reset bootstrap flags so
     * `init` can run again (logout, tab demotion, etc.).
     */
    terminate(container?: HTMLElement | null): void {
        ccpLogger.info('streams.bootstrap.terminate');
        terminateCcp(container ?? _lastBootstrapConfig?.container ?? null);
        resetBootstrapFlags();
    },

    /**
     * After logout: tear down the current CCP session and mount a fresh hidden iframe.
     */
    async reinitAfterLogout(container?: HTMLElement | null): Promise<void> {
        const host = container ?? _lastBootstrapConfig?.container;
        const cfg = _lastBootstrapConfig;
        if (!host || !cfg) {
            ccpLogger.warn('reinitAfterLogout skipped — no bootstrap config');
            return;
        }
        this.terminate(host);
        await this.init({ ...cfg, container: host });
    },

    /**
     * Trigger Connect login without a full page reload (requires active CCP iframe).
     */
    reauthenticate(): void {
        const reauth = (connectClient.core as { reauthenticateAfterLogout?: () => void })
            .reauthenticateAfterLogout;
        if (typeof reauth !== 'function') {
            throw new Error('reauthenticateAfterLogout is not available on this Streams build');
        }
        streamsStore.onAuthorizing();
        reauth.call(connectClient.core);
    },

    isInitialized(): boolean {
        return _initCalled;
    },

    getLastConfig(): BootstrapConfig | null {
        return _lastBootstrapConfig;
    },
};

// Test-only reset hook.
export function __resetStreamsBootstrapForTests() {
    resetBootstrapFlags();
    _lastBootstrapConfig = null;
}
